import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Tooltip
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import ResponsiveContainer from '../../../components/ResponsiveContainer';
import TableAndFilters from '../../../components/TableAndFilters';
import {
  TIPOS_COMBUSTIBLE,
  TIPOS_DETALLE_VEHICULO,
  TIPOS_TRACCION,
  TIPOS_TRANSMISION,
  TIPOS_VEHICULO_SUBMODELO
} from '../../../constants';
import { errorHandler } from '../../../utils';
import { instanceRef } from '../../../utils/axios';
import { fieldTypes, filterConditions } from '../../../utils/filter-utils';

const { Option } = Select;

function Submodelos() {
  const tableRef = useRef();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [marcas, setMarcas] = useState([]);
  const [marca, setMarca] = useState(null);
  const [modelo, setModelo] = useState(null);
  const [modelos, setModelos] = useState([]);

  const [defaultFilters, setDefaultFilters] = useState({
    id_modelo: {
      value: -1,
      filterType: fieldTypes.NUMERIC,
      filterCondition: filterConditions.EQUALS
    }
  });

  const handleClose = () => {
    setEditData(null);
    setIsModalOpen(false);
  };

  const getMarcas = async () => {
    setLoading(true);
    try {
      const response = await instanceRef.instance.get('/marca/get');
      setMarcas(response.data.data);
      setLoading(false);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const getModelosByMarca = async () => {
    setLoading(true);
    try {
      const response = await instanceRef.instance.get(`/modelo/marca/${marca}`);
      setModelos(response.data.data);
      setLoading(false);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      const url = editData ? '/submodelo/update' : '/submodelo/add';
      await instanceRef.instance.post(url, {
        submodelo: { ...values, id_modelo: modelo }
      });
      message.success('Guardado exitosamente!');
      tableRef.current.reload();
      form.resetFields();
      setEditData(null);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (marca) getModelosByMarca();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marca]);

  useEffect(() => {
    getMarcas();
  }, []);

  const ModalCrearSubmodelo = () => {
    return (
      <Modal
        title="Agregar Submodelo"
        open={isModalOpen}
        onCancel={handleClose}
        footer={
          <Button
            type="primary"
            onClick={() => {
              form.validateFields().then((values) => {
                handleFormSubmit(values);
                handleClose();
              });
            }}
          >
            Guardar
          </Button>
        }
      >
        <Spin spinning={loading} size="large" tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            initialValues={{ id_marca_view: marca, id_modelo_view: modelo }}
          >
            <Row justify="start" gutter={16}>
              <Col xs={12} md={12}>
                <Form.Item label="Marca" name="id_marca_view">
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    disabled
                  >
                    {marcas.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12} md={12}>
                <Form.Item label="Modelo" name="id_modelo_view">
                  <Select
                    showSearch
                    filterOption={(input, option) =>
                      option.props.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    disabled
                  >
                    {modelos.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={24}>
                <Form.Item
                  name="descripcion"
                  label="Descripción"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Input
                    onChange={(e) => {
                      const descripcion = e.target.value.toUpperCase();
                      form.setFieldsValue({ descripcion });
                      form.validateFields(['descripcion']);
                    }}
                    placeholder="Ingrese descripción"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={24}>
                <Form.Item
                  name="descripcion_tipo"
                  label="Tipo Vehículo"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Tipo de vehículo"
                    loading={loading}
                  >
                    {TIPOS_VEHICULO_SUBMODELO.map((item) => (
                      <Option
                        key={`${item.codigo}`}
                        value={`${item.codigo}-${item.descripcion}`}
                      >
                        {item.descripcion}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="plazas"
                  label="Plazas"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <InputNumber min={1} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="plazas_min"
                  label="N# Min de Plazas"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <InputNumber min={1} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="motor"
                  label="Motor"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <InputNumber step={100} min={100} />
                </Form.Item>
              </Col>
              <Col xs={12} md={12}>
                <Form.Item
                  name="combustible_tipo"
                  label="Tipo de Combustible"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Tipo de combustible"
                    loading={loading}
                  >
                    {TIPOS_COMBUSTIBLE.map((item) => (
                      <Option key={`${item.codigo}`} value={`${item.codigo}`}>
                        {item.codigo}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12} md={12}>
                <Form.Item
                  name="traccion"
                  label="Tracción"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Tipo de tracción"
                    loading={loading}
                  >
                    {TIPOS_TRACCION.map((item) => (
                      <Option key={`${item}`} value={`${item}`}>
                        {item}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12} md={12}>
                <Form.Item
                  name="transmision"
                  label="Transmisión"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Tipo de transmisión"
                    loading={loading}
                  >
                    {TIPOS_TRANSMISION.map((item) => (
                      <Option key={`${item}`} value={`${item}`}>
                        {item}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={12} md={12}>
                <Form.Item
                  name="detalle_tipo"
                  label="Detalle tipo"
                  rules={[
                    { required: true, message: 'Este campo es requerido' }
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    placeholder="Seleccionar Tipo de detalle"
                    loading={loading}
                  >
                    {TIPOS_DETALLE_VEHICULO.map((item) => (
                      <Option key={`${item}`} value={`${item}`}>
                        {item}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="id" label="" style={{ display: 'none' }}>
              <Input />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    );
  };

  const columnas = [
    {
      title: 'Código',
      dataIndex: ['codigo']
    },
    {
      title: 'Descripción',
      dataIndex: ['descripcion'],
      filterType: fieldTypes.STRING
    },
    {
      title: 'Tipo de vehículo',
      dataIndex: ['descripcion_tipo'],
      filterType: fieldTypes.STRING
    },
    {
      title: 'Plazas',
      dataIndex: ['plazas']
    },
    {
      title: 'Plazas Min',
      dataIndex: ['plazas_min']
    },
    {
      title: 'Motor',
      dataIndex: ['motor']
    },
    {
      title: 'Tipo de combustible',
      dataIndex: ['combustible'],
      filterType: fieldTypes.STRING
    },
    {
      title: 'Tracción',
      dataIndex: ['traccion']
    },
    {
      title: 'Transmisión',
      dataIndex: ['transmision']
    },
    {
      title: 'Detalle',
      dataIndex: ['detalle_tipo'],
      width: '20%'
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (record) => {
        return (
          <Space>
            <Tooltip title="Editar">
              <Button
                type="text"
                icon={<EditOutlined />}
                size="middle"
                onClick={() => {
                  form.setFieldsValue({
                    id: record.id,
                    descripcion: record.descripcion,
                    descripcion_tipo: `${record.codigo_tipo}-${record.descripcion_tipo}`,
                    plazas: record.plazas,
                    plazas_min: record.plazas_min,
                    id_modelo: record.id_modelo,
                    combustible_tipo: record.combustible,
                    traccion: record.traccion,
                    transmision: record.transmision,
                    detalle_tipo: record.detalle_tipo
                  });
                  setEditData(true);
                  setIsModalOpen(true);
                }}
              />
            </Tooltip>
            <Tooltip title="Eliminar">
              <DeleteOutlined style={{ color: 'rgba(0,0,0,.45)' }} disabled />
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  return (
    <ResponsiveContainer size="large" style={{ paddingTop: 0 }}>
      <Card style={{ marginBottom: 5, textAlign: 'left' }} title="Filtros">
        <Form
          scrollToFirstError
          layout="horizontal"
          name="filtro"
          autoComplete="off"
        >
          <Row justify="start" gutter={16}>
            <Col xs={12} md={12}>
              <Form.Item label="Marca" name="id_marca">
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  onChange={(value) => {
                    setMarca(value);
                    setModelo(null);
                    form.setFieldsValue({ id_modelo: null });
                  }}
                >
                  {marcas.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} md={12}>
              <Form.Item label="Modelo" name="id_modelo">
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option.props.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  onChange={(value) => {
                    setDefaultFilters({
                      id_modelo: {
                        value: Number(value),
                        filterType: fieldTypes.NUMERIC,
                        filterCondition: filterConditions.EQUALS
                      }
                    });
                    setTimeout(() => {
                      tableRef.current.reload();
                    }, 500);
                    setModelo(value);
                  }}
                >
                  {modelos.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
      <TableAndFilters
        ref={tableRef}
        withGeneralFilter
        url="/submodelo"
        title={`
               Gestión de Submodelos`}
        columns={columnas}
        showTable
        hasExportar
        extra={[
          <Button
            key="agregarSubmodelo"
            type="dashed"
            shape="round"
            icon={<PlusCircleOutlined />}
            size="middle"
            onClick={() => {
              form.resetFields();
              setIsModalOpen(true);
            }}
            disabled={!marca || !modelo}
          >
            Agregar Submodelo
          </Button>
        ]}
        defaultFilters={defaultFilters}
      />
      <ModalCrearSubmodelo />
    </ResponsiveContainer>
  );
}

export default Submodelos;
